import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, CheckCircle2, RotateCcw, MessageSquare } from "lucide-react";

export default function BookingActionsModal({ booking, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(null);

  if (!booking) return null;

  const act = async (verb, body) => {
    setLoading(verb);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/${verb}`, body || {});
      onUpdate(data);
      const msgs = {
        cancel: "Booking cancelled",
        "mark-active": "Booking marked active",
        refund: "Payment refunded",
        notes: "Note added",
      };
      toast.success(msgs[verb] || "Done");
      if (verb === "notes") setNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setLoading(null); }
  };

  const confirmAct = (verb, msg, body) => {
    if (window.confirm(msg)) act(verb, body);
  };

  const refunded = booking.refund_status === "refunded";
  const canRefund = booking.payment_status === "paid" && !refunded;
  const canMarkActive = booking.payment_status === "paid" && booking.status !== "active" && booking.status !== "cancelled";
  const canCancel = booking.status !== "cancelled";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B0C0E]/40 backdrop-blur-sm px-4" data-testid="booking-actions-modal">
      <div className="bg-white border-2 border-[#0B0C0E] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[#E5E3DA] px-6 py-4">
          <div>
            <div className="overline">Booking Actions</div>
            <div className="mt-1 font-display font-black text-xl uppercase tracking-tight text-[#0B0C0E]">
              Lot {booking.lot_number} · {booking.tenant_name}
            </div>
          </div>
          <button onClick={onClose} className="text-[#56606E] hover:text-[#0B0C0E]" data-testid="close-actions-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Quick summary */}
          <div className="grid grid-cols-2 gap-px bg-[#E5E3DA] border border-[#E5E3DA] text-xs">
            <Mini label="Status" value={booking.status.replace("_", " ").toUpperCase()} />
            <Mini label="Payment" value={booking.payment_status.toUpperCase()} />
            <Mini label="Total" value={`$${booking.total_due.toLocaleString()}`} />
            <Mini label="Refund" value={refunded ? "REFUNDED" : "—"} />
          </div>

          {/* Actions */}
          <div>
            <div className="overline mb-3">Actions</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <ActionButton
                label="Mark Active"
                icon={<CheckCircle2 className="w-4 h-4" />}
                disabled={!canMarkActive || loading === "mark-active"}
                onClick={() => confirmAct("mark-active", "Force this booking to ACTIVE? Use this only if the tenant signed offline.")}
                loading={loading === "mark-active"}
                testId="action-mark-active"
              />
              <ActionButton
                label="Refund Payment"
                icon={<RotateCcw className="w-4 h-4" />}
                disabled={!canRefund || loading === "refund"}
                onClick={() => confirmAct("refund", `Refund the full $${booking.total_due.toLocaleString()} via Stripe? This also cancels the booking.`)}
                loading={loading === "refund"}
                variant="warning"
                testId="action-refund"
              />
              <ActionButton
                label="Cancel Booking"
                icon={<X className="w-4 h-4" />}
                disabled={!canCancel || loading === "cancel"}
                onClick={() => confirmAct("cancel", "Cancel this booking? The lot will be freed up. (Does not issue a refund — use Refund for that.)")}
                loading={loading === "cancel"}
                variant="danger"
                testId="action-cancel"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className="overline mb-3 flex items-center gap-2">
              <MessageSquare className="w-3 h-3" /> Admin Notes
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto mb-3" data-testid="admin-notes-list">
              {(booking.admin_notes || []).length === 0 && (
                <div className="text-xs text-[#97A1AE] italic">No notes yet.</div>
              )}
              {(booking.admin_notes || []).map((n, i) => (
                <div key={`${n.at}-${i}`} className="border-l-2 border-[#FACC15] bg-[#FCFBF6] p-3 text-sm">
                  <div className="text-xs text-[#56606E] flex items-center justify-between">
                    <span>{n.by}</span>
                    <span>{new Date(n.at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-[#0B0C0E]">{n.note}</div>
                </div>
              ))}
            </div>
            <textarea
              rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (e.g. tenant called, asked about extension)…"
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="add-note-input"
            />
            <button
              onClick={() => note.trim() && act("notes", { note: note.trim() })}
              disabled={!note.trim() || loading === "notes"}
              className="mt-2 bg-[#0B0C0E] hover:bg-black disabled:opacity-50 text-white font-bold px-5 py-2 text-xs uppercase tracking-wide inline-flex items-center gap-2"
              data-testid="add-note-btn"
            >
              {loading === "notes" ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Add Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div className="bg-white p-3">
    <div className="overline">{label}</div>
    <div className="mt-1 text-[#0B0C0E] font-medium">{value}</div>
  </div>
);

const ActionButton = ({ label, icon, onClick, disabled, loading, variant = "default", testId }) => {
  const styles = {
    default: "border-[#0B0C0E] bg-white text-[#0B0C0E] hover:bg-[#FACC15]",
    warning: "border-amber-700 bg-white text-amber-700 hover:bg-amber-50",
    danger: "border-red-700 bg-white text-red-700 hover:bg-red-50",
  }[variant];
  return (
    <button
      onClick={onClick} disabled={disabled}
      className={`border-2 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-3 text-xs uppercase font-bold tracking-wide inline-flex items-center justify-center gap-2 transition ${styles}`}
      data-testid={testId}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : icon} {label}
    </button>
  );
};
